import { fade, hex, rgba, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import type { CaptionButton, ScrollBarParts, VisualState, WindowVisualState } from "./Theme.js";

/**
 * "Minimal" — monochrome, hairlines, and a lot of air.
 *
 * The only ink is near-black on white, so the accent is the ink itself:
 * default buttons, checks and selection all invert rather than color in.
 * Chrome is stripped to single-pixel rules — there is no title-bar fill, no
 * shadow to speak of, and buttons carry no fill until you point at them. What
 * separates things is spacing and one hairline, never a box inside a box.
 */

const INK = hex("#111111");
const HAIRLINE = hex("#e4e4e4");
const RULE = hex("#d6d6d6");
const MINIMAL_FONT = "'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif";

export class MinimalTheme extends BaseTheme {
  constructor() {
    super({
      name: "minimal",
      isDark: false,
      metrics: makeMetrics({
        // The air comes from padding, which costs an app nothing. The metrics
        // that decide how much room a window needs — title bar, row heights —
        // stay close to the other themes so a fixed layout still fits.
        titleBarHeight: 36,
        windowBorder: 1,
        windowCornerRadius: 0,
        resizeGripSize: 6,
        captionButtonWidth: 46,
        controlCornerRadius: 0,
        controlHeight: 30,
        buttonPadding: insets(18, 7),
        buttonMinWidth: 96,
        focusRingWidth: 1,
        focusRingInset: -4,
        textBoxPadding: insets(12, 6),
        checkBoxSize: 16,
        radioSize: 16,
        checkLabelGap: 10,
        scrollBarSize: 10,
        scrollBarMinThumb: 32,
        listItemHeight: 27,
        listPadding: insets(1),
        headerHeight: 31,
        columnPadding: 12,
        dialogPadding: 24,
        dialogButtonGap: 10,
        font: { family: MINIMAL_FONT, size: 13 },
        // No bold caption: the title is just another line of text.
        captionFont: { family: MINIMAL_FONT, size: 13, weight: 400 },
        monoFont: { family: "'Cascadia Mono', Consolas, monospace", size: 12.5 },
      }),
      palette: derivePalette({
        desktop: hex("#d5d5d2"),
        surface: hex("#ffffff"),
        // Buttons share the window's color, so they read as text until hovered.
        raised: hex("#ffffff"),
        field: hex("#ffffff"),
        ink: INK,
        accent: INK,
        accentText: hex("#ffffff"),
        border: HAIRLINE,
        titleBar: hex("#ffffff"),
        titleText: INK,
        isDark: false,
        overrides: {
          windowBorderActive: RULE,
          windowBorderInactive: HAIRLINE,
          titleTextInactive: hex("#9a9a9a"),
          controlBgHover: hex("#f4f4f4"),
          controlBgPressed: hex("#eaeaea"),
          controlBgDisabled: hex("#ffffff"),
          controlBorder: hex("#dcdcdc"),
          controlBorderHover: hex("#a8a8a8"),
          fieldBorder: hex("#dcdcdc"),
          fieldBorderFocused: INK,
          textSubtle: hex("#8a8a8a"),
          textDisabled: hex("#c0c0c0"),
          selectionBg: INK,
          selectionText: hex("#ffffff"),
          selectionInactiveBg: hex("#ececec"),
          textSelectionBg: fade(INK, 0.16),
          scrollTrack: rgba(0, 0, 0, 0),
          scrollThumb: fade(INK, 0.18),
          scrollThumbHover: fade(INK, 0.34),
          scrollThumbPressed: fade(INK, 0.52),
          headerBg: hex("#ffffff"),
          headerBgHover: hex("#fafafa"),
          headerText: hex("#767676"),
          gridLine: hex("#eeeeee"),
          focusRing: INK,
          // Barely there: enough to lift a window off the desktop, no more.
          shadow: rgba(0, 0, 0, 0.1),
        },
      }),
    });
  }

  /** One hairline under the caption, in place of a title-bar fill. */
  protected override decorateWindow(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const b = this.metrics.windowBorder;
    rc.hLine(
      b,
      b + this.captionHeight(s) - 1,
      bounds.width - b * 2,
      s.active ? RULE : HAIRLINE,
    );
  }

  /** Even the close button stays monochrome — it inverts rather than reddens. */
  protected override captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    if (button === "close") return pressed ? hex("#000000") : INK;
    return pressed ? hex("#eaeaea") : hex("#f4f4f4");
  }

  /** A single rule below the header; no fill, no dividers between columns. */
  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    if (s.pressed) rc.fillRect(r, p.controlBgPressed);
    else if (s.hovered) rc.fillRect(r, p.headerBgHover);
    rc.hLine(r.x, r.y + r.height - 1, r.width, RULE);
  }

  /** A hairline box, whether sunken or raised — the distinction is not made. */
  override drawPanel(rc: RenderContext, r: Rect, _sunken: boolean): void {
    rc.drawRect(r, { fill: this.palette.windowBg, borderWidth: 1, borderColor: HAIRLINE });
  }

  /** No track, and the thumb is a plain bar rather than a capsule. */
  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void {
    if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return;
    const p = this.palette;
    const hovered = parts.hoveredPart === "thumb" || s.hovered;
    const pressed = parts.pressedPart === "thumb";
    const color = pressed ? p.scrollThumbPressed : hovered ? p.scrollThumbHover : p.scrollThumb;

    // Idle it is a 2px rule hugging the edge; hovering thickens it.
    const thick = hovered || pressed ? 6 : 2;
    const t = parts.thumb;
    const r: Rect = parts.vertical
      ? { x: t.x + (t.width - thick) / 2, y: t.y, width: thick, height: t.height }
      : { x: t.x, y: t.y + (t.height - thick) / 2, width: t.width, height: thick };
    rc.fillRect(r, color);
  }

  /** A thin square outline, offset clear of the control. */
  override drawFocusRing(rc: RenderContext, r: Rect): void {
    const i = this.metrics.focusRingInset;
    rc.strokeRect(
      { x: r.x + i, y: r.y + i, width: r.width - i * 2, height: r.height - i * 2 },
      INK,
      1,
      0,
    );
  }
}
